import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation, Trans } from "react-i18next";

import { CapsuleButton } from "../_components/CapsuleButton";
import { technicalServiceActions } from "../_actions/technicalService.actions";
import { Selectors } from "../_reducers/app.reducer";
import { appPermissions } from "../_constants/permissions.constants";
import { LOCAL_INVITATION_STATUS } from "../_constants/invitations.constants";
import { TimeHelper } from "../_helpers/TimeHelper";

// Whether the pending-code message tells the user to call the SAT phone
// (`global_phone_number_sat`) or just to hand the code to a technician.
const SHOW_SAT_PHONE_NUMBER = false;

/**
 * Request, list and revoke the technical support (SAT) accesses of a location.
 *
 * Without props it works on the location selected in the Everou slice, as in
 * the Everou settings. `locationUid` and `permission` let another module (PMS,
 * whose selected location lives in its own slice) point it at a location
 * without hydrating that slice. `isEmbedded` drops the module's own frame and
 * title for hosts that already draw them around it.
 */
export function TechnicalServiceModule({
    locationUid: locationUidProp = null,
    permission: permissionProp = null,
    isEmbedded = false,
} = {}) {
    //LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();

    //GLOBAL STATE
    const everouLocationUid = useSelector(Selectors.getSelectedLocationUid);
    const everouPermission  = useSelector(Selectors.getPermissionSelectedLocation);
    const satInvitations    = useSelector(Selectors.getSatInvitations);
    const satLocationUid    = useSelector(Selectors.getSatInvitationsLocationUid);
    const myUserUid         = useSelector(Selectors.getUserUid);

    //LOCAL STATE
    const locationUid = locationUidProp ?? everouLocationUid;
    const permission = locationUidProp ? permissionProp : everouPermission;
    const canRequestSat = appPermissions.canRequestSat(permission);
    const satPhoneNumber = t('global_phone_number_sat');
    // The list in the store may still belong to the previous location while
    // this one's is in flight; showing it would attach its codes to the
    // wrong location.
    const invitations = satLocationUid === locationUid ? satInvitations : [];

    //INITIALIZATION
    useEffect(() => {
        dispatch(technicalServiceActions.clearSatInvitations());
        // Explicit uid only when the host gave one: the Everou path keeps
        // resolving (and permission-checking) against its own slice.
        dispatch(technicalServiceActions.loadSatInvitations(locationUidProp));
    }, [dispatch, locationUid, locationUidProp]);

    //VIEW
    if (!canRequestSat)
        return null;

    return invitations.length
        ? withInvitations()
        : empty()
    ;

    function empty() {
        return (
            <div className="
                d-flex
                justify-content-center
                p-2
            ">
                {requestSatButton(t("location_info_sat_request_sat"))}
            </div>
        );
    }

    function withInvitations() {
        return (
            <div className={isEmbedded
                ? "d-flex flex-column"
                : "d-flex flex-column border rounded p-2 my-2"
            }>
                {!isEmbedded && titleFn()}
                {invitations.map(invitationRow)}
                <div className="mt-2"/>
                <div className="d-flex justify-content-center">
                    {requestSatButton(t("location_info_sat_generate_another"))}
                </div>
            </div>
        );
    }

    function titleFn() {
        return (
            <div className="
                text-center
                font-weight-bold
                c-new-ft-l

                my-auto
            ">
                {t("location_info_sat_title")}
            </div>
        );
    }

    function pendingMessageFn(invitation) {
        if (!SHOW_SAT_PHONE_NUMBER) {
            return (
                <Trans
                    i18nKey="location_info_sat_dialog_code"
                    values={{ code: invitation.code }}
                >
                    <strong>code placeholder</strong>
                </Trans>
            );
        }

        return (
            <Trans
                i18nKey="location_info_sat_dialog_pw"
                values={{
                    phoneNumber: satPhoneNumber,
                    code: invitation.code,
                }}
            >
                <strong className="text-nowrap">phone placeholder</strong>
                <strong>code placeholder</strong>
            </Trans>
        );
    }

    function invitationRow(invitation) {
        const isPending =
            invitation.localStatus === LOCAL_INVITATION_STATUS.REQUESTED;

        return (
            <div
                key={invitation.uid}
                className="
                    d-flex
                    align-items-center
                    justify-content-between
                    border-top
                    py-2
                    mt-2
                "
            >
                <div className="pr-2 flex-grow-1" style={{ minWidth: 0 }}>
                    <div>
                        {isPending
                            ? pendingMessageFn(invitation)
                            : t("location_info_sat_in_use_by", {
                                email: invitation.email || invitation.userName,
                            })
                        }
                    </div>
                    {expiryFn(invitation)}
                </div>
                {disableButtonFn(invitation, isPending)}
            </div>
        );
    }

    function disableButtonFn(invitation, isPending) {
        // Your own accepted access is not revokable from the list
        if (!isPending && invitation.userUid === myUserUid)
            return null;

        return (
            <div className="flex-shrink-0">
                <CapsuleButton
                    text={isPending
                        ? t("location_info_sat_dialog_cancel_request")
                        : t("location_info_sat_revoke_access")
                    }
                    onClick={() => handleDisableTechnicalSupport(invitation.uid)}
                    style={`DANGER`}
                    size={`SMALL`}
                />
            </div>
        );
    }

    function expiryFn(invitation) {
        if (!invitation.end)
            return null;

        return (
            <div className="small text-secondary">
                <Trans
                    i18nKey="location_info_sat_expires_at"
                    values={{
                        date: TimeHelper.isoStringToLocalTimezoneFormat(
                            invitation.end,
                            TimeHelper.getTimeFormats().DATE_NO_SECONDS,
                        ),
                    }}
                >
                    <span className="text-nowrap">date placeholder</span>
                </Trans>
            </div>
        );
    }

    function requestSatButton(text) {
        return (
            <CapsuleButton
                text={text}
                onClick={handleRequestTechnicalSupport}
            />
        );
    }

    //ACTION HANDLERS
    function handleRequestTechnicalSupport() {
        dispatch(technicalServiceActions.requestSat(locationUidProp));
    }

    function handleDisableTechnicalSupport(invitationUid) {
        dispatch(technicalServiceActions.disableSat(invitationUid, locationUidProp));
    }
}
